import { atom, computed } from 'nanostores';

import { clamp } from '@iconik-io/core/src/clamp';

import {
  countTextMatches,
  type PageTextContent,
} from '../../utils/count_text_matches.js';
import type { createPlayableMediaStore } from './playable_media.js';
import type { createPlaylistStore } from './playlist.js';

export type PagesTextContent = {
  [pageNumber: number]: PageTextContent[];
};

export type MatchedPageTextsWithQuery = {
  query: string;
  matchedTexts: PagesTextContent;
};

export type SearchOptions = {
  visible: boolean;
  term: string;
  highlightColor: string;
  currentHighlightColor: string;
  matchCase: boolean;
  matchWholeWord: boolean;
};

// Slides are zero indexed and interpreted as milliseconds on virtual timeline.
export function createSlidesStore<PlaylistItemMeta>(
  playlist: ReturnType<typeof createPlaylistStore<PlaylistItemMeta>>,
  playableMedia: ReturnType<typeof createPlayableMediaStore<PlaylistItemMeta>>,
) {
  const clampSlide = (slideIndex: number) => {
    return clamp(Math.round(slideIndex), 0, $slidesCount.get() - 1);
  };

  const $currentSlide = computed([playableMedia.$currentTime], (currentTime) =>
    clampSlide((currentTime ?? 0) * 1000),
  );

  const $slidesCount = computed(
    [playableMedia.$duration],
    (duration) => (duration ?? 0) * 1000,
  );

  const setSlidesCount = (count: number) => {
    playableMedia.setDuration(count / 1000);
  };

  const $previousSlideAvailable = computed(
    [$currentSlide],
    (currentSlide) => currentSlide > 0,
  );

  const $nextSlideAvailable = computed(
    [$currentSlide, $slidesCount],
    (currentSlide, maxSlide) => currentSlide < maxSlide - 1,
  );

  function print() {
    const pdfUrl = playlist.$currentPlaylistItem.get()?.url;
    if (!pdfUrl) {
      return;
    }
    const printWindow = window.open(
      `/static/pdfjs/web/viewer.html?file=${encodeURIComponent(pdfUrl)}`,
      '_blank',
    );

    setTimeout(() => {
      printWindow?.print();
    }, 3000);
  }

  const getInitialSearchOptions = () => {
    try {
      const stored = localStorage.getItem('UP_pdf_search_options');
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  };

  const $searchOptions = atom<SearchOptions>({
    visible: false,
    term: '',
    highlightColor: 'rgba(123, 131, 230, 0.65)',
    currentHighlightColor: 'rgba(126, 170, 128, 0.65)',
    matchCase: false,
    matchWholeWord: false,
    ...getInitialSearchOptions(),
  });

  const setSearchOptions = (options: Partial<SearchOptions>) => {
    const prevTerm = $searchOptions.get().term;
    const prevMatchCase = $searchOptions.get().matchCase;
    const prevMatchWholeWord = $searchOptions.get().matchWholeWord;

    $searchOptions.set({
      ...getSearchOptions(),
      ...options,
    });
    if (
      (options.matchCase !== undefined &&
        options.matchCase !== prevMatchCase) ||
      (options.matchWholeWord !== undefined &&
        options.matchWholeWord !== prevMatchWholeWord)
    ) {
      // If the changed search option is matchCase or mathWholeWord, save the value to local storage
      // so that it can be restored when the player is reloaded
      if (
        options.matchCase !== undefined ||
        options.matchWholeWord !== undefined
      ) {
        const { matchCase, matchWholeWord } = {
          ...getSearchOptions(),
          ...options,
        };
        localStorage.setItem(
          'UP_pdf_search_options',
          JSON.stringify({ matchCase, matchWholeWord }),
        );
      }
    } else if (options.term !== undefined && options.term !== prevTerm) {
      // Set to the first matched item on the current page, or 0 if none
      const matchedPageTexts = $matchedPageTexts.get();
      const currentSlide = $currentSlide.get();
      if (
        matchedPageTexts &&
        matchedPageTexts.matchedTexts &&
        matchedPageTexts.matchedTexts[currentSlide] &&
        matchedPageTexts.matchedTexts[currentSlide].length > 0
      ) {
        // Find the index of the first match on the current page
        let index = 0;
        const matchedTexts = matchedPageTexts.matchedTexts;
        const sortedPages = Object.keys(matchedTexts)
          .map(Number)
          .sort((a, b) => a - b);
        for (const pageNum of sortedPages) {
          if (pageNum === currentSlide) {
            break;
          }
          index += countTextMatches(matchedTexts[pageNum]);
        }
        $_currentMatchedIndex.set(index);
      } else {
        $_currentMatchedIndex.set(0);
      }
    }
  };

  const getSearchOptions = () => {
    const localOptions = (() => {
      try {
        const stored = localStorage.getItem('UP_pdf_search_options');
        return stored ? JSON.parse(stored) : {};
      } catch {
        return {};
      }
    })();
    return {
      ...$searchOptions.get(),
      ...localOptions,
    };
  };

  const $matchedPageTexts = atom<MatchedPageTextsWithQuery | undefined>(
    undefined,
  );

  const setMatchedPageTexts = (
    matchedPageTexts: MatchedPageTextsWithQuery | undefined,
  ) => {
    $matchedPageTexts.set(matchedPageTexts);
  };

  const $_currentMatchedIndex = atom<number>(0);
  const setCurrentMatchedIndex = (index: number) => {
    const total = $totalMatchedItems.get();
    if (index < -1 || index >= total) {
      return;
    }
    $_currentMatchedIndex.set(index);
  };
  const addToMatchedIndex = (offset: number) => {
    const total = $totalMatchedItems.get();
    if (total === 0) {
      return;
    }
    const current = $_currentMatchedIndex.get();
    let next = current + offset;
    if (next < 0) {
      next = 0;
    }
    if (next >= total) {
      next = total - 1;
    }
    $_currentMatchedIndex.set(next);
  };

  const getPageNumberFromMatchIndex = (index: number) => {
    const matchedPageTexts = $matchedPageTexts.get();
    if (!matchedPageTexts?.matchedTexts) {
      return -1;
    }
    let sum = 0;
    let count = 0;
    const pages = Object.keys(matchedPageTexts.matchedTexts)
      .map(Number)
      .sort((a, b) => a - b);
    for (const pageNum of pages) {
      const texts = matchedPageTexts.matchedTexts[pageNum];
      count = countTextMatches(texts);
      if (index < sum + count) {
        return pageNum;
      }
    }
    sum += count;
    return -1;
  };

  const $totalMatchedItems = computed(
    [$matchedPageTexts],
    (matchedPageTexts) => {
      if (!matchedPageTexts?.matchedTexts) {
        return 0;
      }
      let sum = 0;
      const pages = Object.values(matchedPageTexts.matchedTexts);
      for (const texts of pages) {
        sum += countTextMatches(texts);
      }
      return sum;
    },
  );

  const $currentMatchedIndex = computed(
    [$_currentMatchedIndex, $totalMatchedItems],
    (currentMatchedIndex, totalMatchedItems) => {
      if (totalMatchedItems === 0) {
        return 0;
      }
      return clamp(currentMatchedIndex, -1, totalMatchedItems - 1);
    },
  );

  const clearSearch = () => {
    setSearchOptions({
      term: '',
      visible: false,
    });
    setCurrentMatchedIndex(0);
  };

  const $pdfNotification = atom<string | null>(null);

  const setPdfNotification = (message: string) => {
    $pdfNotification.set(message);
  };

  const clearPdfImageCopyNotification = () => {
    $pdfNotification.set(null);
  };

  return {
    $currentSlide,
    $slidesCount,
    setSlidesCount,
    $previousSlideAvailable,
    $nextSlideAvailable,
    print,
    $searchOptions,
    setSearchOptions,
    $matchedPageTexts,
    setMatchedPageTexts,
    $currentMatchedIndex,
    $totalMatchedItems,
    addToMatchedIndex,
    clearSearch,
    setCurrentMatchedIndex,
    getPageNumberFromMatchIndex,
    clampSlide,
    $pdfNotification,
    setPdfNotification,
    clearPdfImageCopyNotification,
  };
}
